import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export interface ExecuteToolboxActionParams {
  integrationConnectionId?: string;
  actionName: string;
  actionInput: Record<string, any>;
  /** Canvas context for actions that need it (e.g. canvas-edit). Sent in body; backend strips into params. */
  canvasId?: string;
  /** Optional flow session ID for canvas context. Sent in body; backend strips into params. */
  flowSessionId?: string;
}

function buildToolboxBody(
  params: ExecuteToolboxActionParams,
): Record<string, any> {
  const body = { ...params.actionInput };
  if (params.canvasId) body.canvas_id = params.canvasId;
  if (params.flowSessionId) body.flow_session_id = params.flowSessionId;
  return body;
}

export function useToolboxAction<T = any>() {
  return useMutation({
    mutationFn: async (params: ExecuteToolboxActionParams) => {
      return await orgAPI.toolbox.execute<T>({
        integration_connection_id: params.integrationConnectionId,
        action_name: params.actionName,
        action_input: buildToolboxBody(params),
      });
    },
  });
}

/**
 * Query-based hook for toolbox actions - useful for fetching lists with caching
 */
export function useToolboxQuery<T = any>(
  params: ExecuteToolboxActionParams | null,
  options?: Partial<UseQueryOptions<T, AxiosError>>,
) {
  return useQuery<T, AxiosError>({
    queryKey: [
      "toolbox",
      params?.integrationConnectionId,
      params?.actionName,
      params?.canvasId,
      params?.flowSessionId,
      params?.actionInput,
    ],
    queryFn: async () => {
      if (!params) throw new Error("Missing params");
      const response = await orgAPI.toolbox.execute<T>({
        integration_connection_id: params.integrationConnectionId,
        action_name: params.actionName,
        action_input: buildToolboxBody(params),
      });
      return response.data;
    },
    enabled:
      !!params?.actionName &&
      (!!params?.integrationConnectionId || !!params?.canvasId),
    ...options,
  });
}
